import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { UserPlus, Users, Copy, Check, Trash2, X, Crown, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
    const { authHeaders, user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviting, setInviting] = useState(false);
    const [copied, setCopied] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/team`, { headers: authHeaders() });
            setOverview(res.data);
        } finally { setLoading(false); }
    }, [authHeaders]);

    useEffect(() => { load(); }, [load]);

    const invite = async (e) => {
        e.preventDefault();
        setInviting(true);
        try {
            await axios.post(`${API}/team/invite`, { email: inviteEmail }, { headers: authHeaders() });
            toast.success(`Invite created for ${inviteEmail}`);
            setInviteEmail("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invite failed");
        } finally { setInviting(false); }
    };

    const removeMember = async (uid) => {
        if (!window.confirm("Remove this teammate? They'll be moved to their own free plan.")) return;
        try {
            await axios.delete(`${API}/team/member/${uid}`, { headers: authHeaders() });
            toast.success("Removed");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Remove failed"); }
    };

    const cancelInvite = async (id) => {
        try {
            await axios.delete(`${API}/team/invite/${id}`, { headers: authHeaders() });
            toast.success("Invite cancelled");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Cancel failed"); }
    };

    const copyInvite = async (token, id) => {
        const url = `${window.location.origin}/accept-invite/${token}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(id);
            toast.success("Invite link copied");
            setTimeout(() => setCopied(null), 1200);
        } catch { toast.error("Copy failed"); }
    };

    if (loading) return <div className="text-zinc-500">Loading team…</div>;

    const isOwner = overview?.owner_id === user?.id;
    const unlimited = !!overview?.admin_unlimited;
    const memberCount = overview?.members?.length || 0;
    const inviteCount = overview?.invites?.length || 0;
    const seatsLeft = unlimited
        ? Infinity
        : (overview?.paid_seats || 1) - memberCount - inviteCount;
    const hasRoom = seatsLeft > 0;

    return (
        <div data-testid="team-page">
            <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ TEAM</div>
                <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Your crew.</h1>
                <p className="text-zinc-400 mt-2">
                    {unlimited ? (
                        <>Unlimited seats — invite anyone, free forever (admin).</>
                    ) : (
                        <>Owner pays — teammates use SalesWhisper for free.{" "}
                            <Link to="/app/billing" className="text-signal hover:underline" data-testid="team-billing-link">Manage seats →</Link>
                        </>
                    )}
                </p>
            </div>

            <div className="border border-zinc-900 bg-[#0c0c0c] p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-signal" strokeWidth={1.5} />
                        <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Capacity</span>
                    </div>
                    <span className="text-xs text-zinc-500 font-mono">
                        {memberCount} member{memberCount === 1 ? "" : "s"}, {inviteCount} pending
                    </span>
                </div>
                <div className="font-heading font-black text-3xl">
                    {unlimited ? (
                        <span data-testid="seats-unlimited" className="inline-flex items-center gap-3">
                            ∞ <span className="text-base font-mono font-normal text-signal uppercase tracking-widest">Unlimited seats</span>
                        </span>
                    ) : hasRoom ? (
                        <span data-testid="seats-available">{seatsLeft} seat{seatsLeft === 1 ? "" : "s"} available</span>
                    ) : (
                        <span className="text-zinc-500" data-testid="seats-full">All seats filled</span>
                    )}
                </div>
            </div>

            {/* Invite form */}
            {isOwner && (
                <div className="border border-zinc-900 bg-[#0c0c0c] p-6 mb-6" data-testid="invite-card">
                    <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-signal" strokeWidth={1.5} /> Invite a teammate
                    </h2>
                    <form onSubmit={invite} className="flex flex-col sm:flex-row gap-3" data-testid="invite-form">
                        <input
                            type="email"
                            required
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="teammate@company.com"
                            data-testid="invite-email-input"
                            className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-3 focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                        />
                        <button
                            type="submit"
                            disabled={inviting || !hasRoom}
                            data-testid="invite-submit-btn"
                            className="bg-signal hover:bg-signal-hover text-white px-6 py-3 rounded-md font-semibold transition-colors disabled:opacity-60"
                        >
                            {inviting ? "Sending…" : "Send invite"}
                        </button>
                    </form>
                    {!unlimited && !hasRoom && (
                        <p className="text-xs text-zinc-500 mt-3">
                            No open seats.{" "}
                            <Link to="/app/billing" className="text-signal hover:underline">Buy more →</Link>
                        </p>
                    )}
                </div>
            )}

            {/* Members */}
            <div className="border border-zinc-900 bg-[#0c0c0c] mb-6">
                <div className="p-4 border-b border-zinc-900 text-xs font-mono uppercase tracking-widest text-zinc-500">Members</div>
                <div className="divide-y divide-zinc-900">
                    {overview?.members?.map((m) => (
                        <div key={m.user_id} className="p-4 flex items-center justify-between" data-testid={`member-${m.user_id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="font-medium text-white truncate">{m.name}</span>
                                    {m.role === "owner" && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono bg-signal/10 text-signal border border-signal/30 uppercase tracking-widest">
                                            <Crown className="w-3 h-3" /> Owner
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                            </div>
                            {isOwner && m.role !== "owner" && (
                                <button
                                    onClick={() => removeMember(m.user_id)}
                                    data-testid={`remove-member-${m.user_id}`}
                                    className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1.5 ml-3"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Pending invites */}
            {overview?.invites?.length > 0 && (
                <div className="border border-zinc-900 bg-[#0c0c0c]">
                    <div className="p-4 border-b border-zinc-900 text-xs font-mono uppercase tracking-widest text-zinc-500">Pending invites</div>
                    <div className="divide-y divide-zinc-900">
                        {overview.invites.map((inv) => (
                            <div key={inv.id} className="p-4 flex items-center justify-between gap-3" data-testid={`invite-${inv.id}`}>
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <Mail className="w-4 h-4 text-zinc-600 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white truncate">{inv.email}</div>
                                        <div className="text-xs text-zinc-500 font-mono">pending · {new Date(inv.created_at).toLocaleDateString()}</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => copyInvite(inv.token, inv.id)}
                                    data-testid={`copy-invite-${inv.id}`}
                                    className="text-xs text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-600 px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
                                >
                                    {copied === inv.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy link</>}
                                </button>
                                {isOwner && (
                                    <button
                                        onClick={() => cancelInvite(inv.id)}
                                        data-testid={`cancel-invite-${inv.id}`}
                                        className="text-zinc-500 hover:text-red-400"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
